import { CommonModule } from "@angular/common";
import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output, output, signal } from "@angular/core";
import { SystemAdminService } from "../../../../services/system-admin.service";
import { FormGroup, ReactiveFormsModule } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { forkJoin, map, Subscription } from "rxjs";
import { BranchDetailsResponse, CompanyDetailsResponse, CountriesList, UserDetailsResponse, UserRoleListResponse } from "../../../../models/SystemAdmin.model";
import { ToastService } from "../../../../services/toast.service";
import { ToastType } from "../../../../enums/config.enum";
import { MatExpansionModule } from "@angular/material/expansion";
import { CountryISO, NgxIntlTelInputModule, SearchCountryField } from 'ngx-intl-tel-input';
import { PhoneNumberUtil, PhoneNumberFormat } from 'google-libphonenumber';

@Component({
    selector: 'app-user-details',
    standalone: true,
    imports: [CommonModule, MatExpansionModule, ReactiveFormsModule, NgxIntlTelInputModule],
    templateUrl: './user-details.component.html',
    styleUrl: './user-details.component.scss'
})
export class UserDetails implements OnInit, OnDestroy{
    @Input() userDetailsForm!: FormGroup;
    @Input() userId!: string;
    @Input() isTabChange!: boolean;
    @Input() isCreateUser!: boolean;
    @Output() isAdminCompany = new EventEmitter<boolean>();  
    @Output() saveClicked = new EventEmitter<void>();
    companyList = signal<CompanyDetailsResponse[]>([]);
    branchList = signal<BranchDetailsResponse[]>([]);
    userRoleList = signal<UserRoleListResponse[]>([]);
    userDetailsSubmitEmitter = output<boolean>(); 
    cancelEmitter = output<boolean>();
    selectedBranchCountryId = signal<number | null>(null);
    pendingBranchId: number | null = null; 
    private isSubmitting: boolean = false;
    countryList = signal<CountriesList[]>([]);
    router = inject(Router);
    activateRoute = inject(ActivatedRoute);
    systemAdminService = inject(SystemAdminService);
    toastService = inject(ToastService);
    companySubscription: Subscription = new Subscription();
    branchSubscription: Subscription = new Subscription();
    mobileInputSubscription: Subscription = new Subscription();
    phoneInputSubscription: Subscription = new Subscription();
    CountryISO = CountryISO;
    SearchCountryField = SearchCountryField;
    phoneUtil = PhoneNumberUtil.getInstance();
    private isPhoneBindingInProgress = false;
    private phoneBindingGuardTimeout: any = null;
    
    // Get min length based on country code - comprehensive mapping
    getMinPhoneLength(countryCode: CountryISO | null | undefined): number {
      if (!countryCode) return 7;
      const regionCode = String(countryCode).toLowerCase();
      
      // Comprehensive country-to-min-length mapping
      // For most countries, min = max (fixed length), but some have ranges
      const countryMinLengths: { [key: string]: number } = {
        // Very short numbers (4-5 digits)
        'nu': 4,  // Niue
        'tk': 4,  // Tokelau
        'nf': 5,  // Norfolk Island
        
        // Short numbers (6-7 digits)
        'gi': 8,  // Gibraltar
        'va': 8,  // Vatican City
        'sm': 9,  // San Marino
        'mc': 9,  // Monaco
        'li': 9,  // Liechtenstein
        
        // Standard 10-digit numbers (US, Canada, etc.)
        'us': 10, // United States
        'ca': 10, // Canada
        'ru': 10, // Russia
        'kz': 10, // Kazakhstan
        'mx': 10, // Mexico
        'br': 10, // Brazil (can be 10 or 11)
        'in': 10, // India
        'au': 9,  // Australia
        'nz': 9,  // New Zealand
        'za': 9,  // South Africa
        'ar': 10, // Argentina
        'cl': 9,  // Chile
        'co': 10, // Colombia
        'pe': 9,  // Peru
        've': 10, // Venezuela
        'ec': 9,  // Ecuador
        'uy': 8,  // Uruguay
        'py': 9,  // Paraguay
        'bo': 8,  // Bolivia
        'cr': 8,  // Costa Rica
        'pa': 8,  // Panama
        'gt': 8,  // Guatemala
        'hn': 8,  // Honduras
        'ni': 8,  // Nicaragua
        'sv': 8,  // El Salvador
        'do': 10, // Dominican Republic
        'cu': 8,  // Cuba
        'jm': 10, // Jamaica
        'tt': 10, // Trinidad and Tobago
        'bb': 10, // Barbados
        'bs': 10, // Bahamas
        'bz': 7,  // Belize
        'gy': 7,  // Guyana
        'sr': 7,  // Suriname
        'gf': 9,  // French Guiana
        
        // 11-digit numbers (UK, etc.)
        'gb': 10, // United Kingdom (10-11 digits)
        'ie': 9,  // Ireland
        'nl': 9,  // Netherlands
        'be': 9,  // Belgium
        'lu': 9,  // Luxembourg
        'ch': 9,  // Switzerland
        'at': 10, // Austria
        'de': 10, // Germany (10-11 digits)
        'dk': 8,  // Denmark
        'se': 9,  // Sweden
        'no': 8,  // Norway
        'fi': 9,  // Finland
        'is': 7,  // Iceland
        'pl': 9,  // Poland
        'cz': 9,  // Czech Republic
        'sk': 9,  // Slovakia
        'hu': 9,  // Hungary
        'ro': 10, // Romania
        'bg': 9,  // Bulgaria
        'gr': 10, // Greece
        'pt': 9,  // Portugal
        'es': 9,  // Spain
        'it': 10, // Italy
        'mt': 8,  // Malta
        'cy': 8,  // Cyprus
        
        // Eastern Europe
        'ua': 9,  // Ukraine
        'by': 9,  // Belarus
        'lt': 8,  // Lithuania
        'lv': 8,  // Latvia
        'ee': 8,  // Estonia
        'md': 8,  // Moldova
        'rs': 9,  // Serbia
        'hr': 9,  // Croatia
        'si': 8,  // Slovenia
        'ba': 8,  // Bosnia and Herzegovina
        'mk': 9,  // North Macedonia
        'al': 9,  // Albania
        'me': 8,  // Montenegro
        'xk': 8,  // Kosovo
        
        // Middle East
        'sa': 9,  // Saudi Arabia
        'ae': 9,  // UAE
        'kw': 8,  // Kuwait
        'qa': 8,  // Qatar
        'bh': 8,  // Bahrain
        'om': 8,  // Oman
        'ye': 9,  // Yemen
        'iq': 10, // Iraq
        'ir': 10, // Iran
        'il': 9,  // Israel
        'jo': 9,  // Jordan
        'lb': 8,  // Lebanon
        'sy': 9,  // Syria
        'ps': 9,  // Palestine
        'tr': 10, // Turkey
        'eg': 10, // Egypt
        'ly': 9,  // Libya
        'tn': 8,  // Tunisia
        'dz': 9,  // Algeria
        'ma': 9,  // Morocco
        'sd': 9,  // Sudan
        'et': 9,  // Ethiopia
        
        // Asia
        'cn': 11, // China
        'jp': 10, // Japan
        'kr': 10, // South Korea
        'tw': 9,  // Taiwan
        'hk': 8,  // Hong Kong
        'mo': 8,  // Macau
        'sg': 8,  // Singapore
        'my': 9,  // Malaysia
        'th': 9,  // Thailand
        'vn': 10, // Vietnam
        'ph': 10, // Philippines
        'id': 9,  // Indonesia
        'mm': 9,  // Myanmar
        'kh': 9,  // Cambodia
        'la': 8,  // Laos
        'bn': 7,  // Brunei
        'bd': 10, // Bangladesh
        'pk': 10, // Pakistan
        'lk': 9,  // Sri Lanka
        'np': 10, // Nepal
        'af': 7,  // Afghanistan
        'uz': 9,  // Uzbekistan
        'tm': 8,  // Turkmenistan
        'tj': 9,  // Tajikistan
        'kg': 9,  // Kyrgyzstan
        'mn': 8,  // Mongolia
        
        // Oceania
        'pg': 8,  // Papua New Guinea
        'fj': 7,  // Fiji
        'nc': 6,  // New Caledonia
        'pf': 6,  // French Polynesia
        'ws': 7,  // Samoa
        'to': 7,  // Tonga
        'vu': 7,  // Vanuatu
        'sb': 7,  // Solomon Islands
        'ki': 5,  // Kiribati
        'tv': 6,  // Tuvalu
        'nr': 7,  // Nauru
        'pw': 7,  // Palau
        'fm': 7,  // Micronesia
        'mh': 7,  // Marshall Islands
        
        // Africa
        'ng': 10, // Nigeria
        'ke': 9,  // Kenya
        'tz': 9,  // Tanzania
        'ug': 9,  // Uganda
        'rw': 9,  // Rwanda
        'gh': 9,  // Ghana
        'ci': 8,  // Côte d'Ivoire
        'sn': 9,  // Senegal
        'ml': 8,  // Mali
        'bf': 8,  // Burkina Faso
        'ne': 8,  // Niger
        'td': 8,  // Chad
        'cm': 9,  // Cameroon
        'ga': 8,  // Gabon
        'cg': 9,  // Republic of the Congo
        'cd': 9,  // DR Congo
        'ao': 9,  // Angola
        'zm': 9,  // Zambia
        'zw': 9,  // Zimbabwe
        'bw': 8,  // Botswana
        'na': 9,  // Namibia
        'mw': 9,  // Malawi
        'mz': 9,  // Mozambique
        'mg': 9,  // Madagascar
        'mu': 8,  // Mauritius
        'sc': 7,  // Seychelles
        'km': 7,  // Comoros
        'dj': 8,  // Djibouti
        'er': 7,  // Eritrea
        'so': 8,  // Somalia
        'ss': 9,  // South Sudan
        
        // Caribbean
        'ht': 8,  // Haiti
        'pr': 10, // Puerto Rico
        'vi': 10, // US Virgin Islands
        'vg': 10, // British Virgin Islands
        'ky': 7,  // Cayman Islands
        'bm': 10, // Bermuda
        'tc': 7,  // Turks and Caicos
        'ai': 7,  // Anguilla
        'ms': 7,  // Montserrat
        'gd': 7,  // Grenada
        'lc': 7,  // Saint Lucia
        'vc': 7,  // Saint Vincent
        'ag': 10,  // Antigua and Barbuda
        'dm': 7,  // Dominica
        'kn': 7,  // Saint Kitts and Nevis
        
        // Other territories
        'fo': 6,  // Faroe Islands
        'gl': 6,  // Greenland
        'sj': 8,  // Svalbard
        'ax': 6,  // Åland Islands
        'gg': 10, // Guernsey
        'je': 10, // Jersey
        'im': 10, // Isle of Man
        'ad': 6,  // Andorra
      };
      
      return countryMinLengths[regionCode] || 7; // Default to 7 for unknown countries
    }
    
    // Get max length based on country code - comprehensive mapping
    getMaxPhoneLength(countryCode: CountryISO | null | undefined): number {
      if (!countryCode) return 15;
      const regionCode = String(countryCode).toLowerCase();
      
      // Comprehensive country-to-max-length mapping
      const countryMaxLengths: { [key: string]: number } = {
        // Very short numbers (4-5 digits)
        'nu': 4,  // Niue
        'tk': 4,  // Tokelau
        'nf': 5,  // Norfolk Island
        
        // Short numbers (6-7 digits)
        'gi': 8,  // Gibraltar
        'va': 8,  // Vatican City
        'sm': 9,  // San Marino
        'mc': 9,  // Monaco
        'li': 9,  // Liechtenstein
        
        // Standard 10-digit numbers (US, Canada, etc.)
        'us': 10, // United States
        'ca': 10, // Canada
        'ru': 10, // Russia
        'kz': 10, // Kazakhstan
        'mx': 10, // Mexico
        'br': 11, // Brazil (can be 10 or 11)
        'in': 10, // India
        'au': 9,  // Australia
        'nz': 9,  // New Zealand
        'za': 9,  // South Africa
        'ar': 10, // Argentina
        'cl': 9,  // Chile
        'co': 10, // Colombia
        'pe': 9,  // Peru
        've': 10, // Venezuela
        'ec': 9,  // Ecuador
        'uy': 8,  // Uruguay
        'py': 9,  // Paraguay
        'bo': 8,  // Bolivia
        'cr': 8,  // Costa Rica
        'pa': 8,  // Panama
        'gt': 8,  // Guatemala
        'hn': 8,  // Honduras
        'ni': 8,  // Nicaragua
        'sv': 8,  // El Salvador
        'do': 10, // Dominican Republic
        'cu': 8,  // Cuba
        'jm': 10, // Jamaica
        'tt': 10, // Trinidad and Tobago
        'bb': 10, // Barbados
        'bs': 10, // Bahamas
        'bz': 7,  // Belize
        'gy': 7,  // Guyana
        'sr': 7,  // Suriname
        'gf': 9,  // French Guiana
        
        // 11-digit numbers (UK, etc.)
        'gb': 11, // United Kingdom
        'ie': 9,  // Ireland
        'nl': 9,  // Netherlands
        'be': 9,  // Belgium
        'lu': 9,  // Luxembourg
        'ch': 9,  // Switzerland
        'at': 10, // Austria
        'de': 11, // Germany
        'dk': 8,  // Denmark
        'se': 9,  // Sweden
        'no': 8,  // Norway
        'fi': 9,  // Finland
        'is': 7,  // Iceland
        'pl': 9,  // Poland
        'cz': 9,  // Czech Republic
        'sk': 9,  // Slovakia
        'hu': 9,  // Hungary
        'ro': 10, // Romania
        'bg': 9,  // Bulgaria
        'gr': 10, // Greece
        'pt': 9,  // Portugal
        'es': 9,  // Spain
        'it': 10, // Italy
        'mt': 8,  // Malta
        'cy': 8,  // Cyprus
        
        // Eastern Europe
        'ua': 9,  // Ukraine
        'by': 9,  // Belarus
        'lt': 8,  // Lithuania
        'lv': 8,  // Latvia
        'ee': 8,  // Estonia
        'md': 8,  // Moldova
        'rs': 9,  // Serbia
        'hr': 9,  // Croatia
        'si': 8,  // Slovenia
        'ba': 8,  // Bosnia and Herzegovina
        'mk': 9,  // North Macedonia
        'al': 9,  // Albania
        'me': 8,  // Montenegro
        'xk': 8,  // Kosovo
        
        // Middle East
        'sa': 9,  // Saudi Arabia
        'ae': 9,  // UAE
        'kw': 8,  // Kuwait
        'qa': 8,  // Qatar
        'bh': 8,  // Bahrain
        'om': 8,  // Oman
        'ye': 9,  // Yemen
        'iq': 10, // Iraq
        'ir': 10, // Iran
        'il': 9,  // Israel
        'jo': 9,  // Jordan
        'lb': 8,  // Lebanon
        'sy': 9,  // Syria
        'ps': 9,  // Palestine
        'tr': 10, // Turkey
        'eg': 10, // Egypt
        'ly': 9,  // Libya
        'tn': 8,  // Tunisia
        'dz': 9,  // Algeria
        'ma': 9,  // Morocco
        'sd': 9,  // Sudan
        'et': 9,  // Ethiopia
        
        // Asia
        'cn': 11, // China
        'jp': 10, // Japan
        'kr': 10, // South Korea
        'tw': 9,  // Taiwan
        'hk': 8,  // Hong Kong
        'mo': 8,  // Macau
        'sg': 8,  // Singapore
        'my': 9,  // Malaysia
        'th': 9,  // Thailand
        'vn': 10, // Vietnam
        'ph': 10, // Philippines
        'id': 9,  // Indonesia
        'mm': 9,  // Myanmar
        'kh': 9,  // Cambodia
        'la': 8,  // Laos
        'bn': 7,  // Brunei
        'bd': 10, // Bangladesh
        'pk': 10, // Pakistan
        'lk': 9,  // Sri Lanka
        'np': 10, // Nepal
        'af': 7,  // Afghanistan
        'uz': 9,  // Uzbekistan
        'tm': 8,  // Turkmenistan
        'tj': 9,  // Tajikistan
        'kg': 9,  // Kyrgyzstan
        'mn': 8,  // Mongolia
        
        // Oceania
        'pg': 8,  // Papua New Guinea
        'fj': 7,  // Fiji
        'nc': 6,  // New Caledonia
        'pf': 6,  // French Polynesia
        'ws': 7,  // Samoa
        'to': 7,  // Tonga
        'vu': 7,  // Vanuatu
        'sb': 7,  // Solomon Islands
        'ki': 5,  // Kiribati
        'tv': 6,  // Tuvalu
        'nr': 7,  // Nauru
        'pw': 7,  // Palau
        'fm': 7,  // Micronesia
        'mh': 7,  // Marshall Islands
        
        // Africa
        'ng': 10, // Nigeria
        'ke': 9,  // Kenya
        'tz': 9,  // Tanzania
        'ug': 9,  // Uganda
        'rw': 9,  // Rwanda
        'gh': 9,  // Ghana
        'ci': 8,  // Côte d'Ivoire
        'sn': 9,  // Senegal
        'ml': 8,  // Mali
        'bf': 8,  // Burkina Faso
        'ne': 8,  // Niger
        'td': 8,  // Chad
        'cm': 9,  // Cameroon
        'ga': 8,  // Gabon
        'cg': 9,  // Republic of the Congo
        'cd': 9,  // DR Congo
        'ao': 9,  // Angola
        'zm': 9,  // Zambia
        'zw': 9,  // Zimbabwe
        'bw': 8,  // Botswana
        'na': 9,  // Namibia
        'mw': 9,  // Malawi
        'mz': 9,  // Mozambique
        'mg': 9,  // Madagascar
        'mu': 8,  // Mauritius
        'sc': 7,  // Seychelles
        'km': 7,  // Comoros
        'dj': 8,  // Djibouti
        'er': 7,  // Eritrea
        'so': 8,  // Somalia
        'ss': 9,  // South Sudan
        
        // Caribbean
        'ht': 8,  // Haiti
        'pr': 10, // Puerto Rico
        'vi': 10, // US Virgin Islands
        'vg': 10, // British Virgin Islands
        'ky': 7,  // Cayman Islands
        'bm': 10, // Bermuda
        'tc': 7,  // Turks and Caicos
        'ai': 7,  // Anguilla
        'ms': 7,  // Montserrat
        'gd': 7,  // Grenada
        'lc': 7,  // Saint Lucia
        'vc': 7,  // Saint Vincent
        'ag': 10,  // Antigua and Barbuda
        'dm': 7,  // Dominica
        'kn': 7,  // Saint Kitts and Nevis
        
        // Other territories
        'fo': 6,  // Faroe Islands
        'gl': 6,  // Greenland
        'sj': 8,  // Svalbard
        'ax': 6,  // Åland Islands
        'gg': 10, // Guernsey
        'je': 10, // Jersey
        'im': 10, // Isle of Man
        'ad': 6,  // Andorra
      };
      
      return countryMaxLengths[regionCode] || 15; // Default to 15 for unknown countries
    }

    ngOnInit(): void {
        this.getCountryList();
        this.getCompanyBranchAndUserRoleList();
        if(this.isTabChange &&this.userId){
           this.getUserDetails(this.userId);
        }

        this.companySubscription = this.userDetailsForm.get('companyId')?.valueChanges.subscribe(value => {
            if (value === "null") {
                this.userDetailsForm.get('companyId')?.setErrors({ required: true });
            }else{
                // Clear error if a valid option is selected
                this.userDetailsForm.get('companyId')?.setErrors(null);
                if(value){
                    this.getBranchDetailsByCompanyId(value);
                }
            }
        }) || new Subscription();

        this.branchSubscription = this.userDetailsForm.get('branchId')?.valueChanges.subscribe(value => {
            if (value === null || value === "null") {
                this.userDetailsForm.get('branchId')?.setErrors({ required: true });
            } else {
                // Clear error if a valid option is selected
                this.userDetailsForm.get('branchId')?.setErrors(null);
                // Update country ID when branch changes
                // Phone number formatting is now handled by add-user component
                if (value) {
                    // Only process if branch list is already loaded
                    if (this.branchList().length > 0) {
                        this.findCountryIdForBranch(value);
                        // If countryCode wasn't set (country list not loaded), retry after a delay
                        setTimeout(() => {
                            const currentCountryCode = this.userDetailsForm.get('countryCode')?.value;
                            if (!currentCountryCode && this.countryList().length > 0) {
                                this.findCountryIdForBranch(value);
                            }
                        }, 200);
                    } else {
                        // Store as pending if branches not loaded yet
                        this.pendingBranchId = value;
                    }
                }
            }
        }) || new Subscription();

        // Live input formatting is now handled by add-user component via valueChanges subscriptions
        // No need for separate formatting here as ngx-intl-tel-input handles it
    }

    getUserDetails(userId: string) {
        this.systemAdminService.getUserDetails(userId).subscribe({
          next:(response: UserDetailsResponse) => {
            if(response){
              this.onBindUserDetailsFormData(response);
            }
          },
          error:(error: any) => {
            this.toastService.addNotification({message: 'System error. Failed to retrieve data. Please exit and contact help@AbacusAU.com.', type: ToastType.Error});
          }
        });
    }


    onBindUserDetailsFormData(formValue: any) {
        console.log("hi");
        
        this.userDetailsForm.patchValue({
            firstName: formValue?.firstName,
            lastName: formValue?.lastName,
            userName: formValue?.userName,
            companyId: formValue?.companyId,
            branchId: formValue?.branchId,
            userRoleId: formValue?.userRoleId,
            userEmail: formValue?.userEmail,  
            userDetailId: formValue?.userDetailId,
            isActive: formValue?.isActive,            
        });
        this.systemAdminService.setSysUserEmailID(formValue?.userEmail); // set userEmailID
        
        // Get phone numbers and convert to string if they're numbers
        const phone = formValue?.phone !== null && formValue?.phone !== undefined ? String(formValue.phone) : null;
        const mobileNumber = formValue?.mobileNumber !== null && formValue?.mobileNumber !== undefined ? String(formValue.mobileNumber) : null;
        
        // Get country codes from response
        // NOTE: Field names are confusing - MobilePhoneCode contains Phone Number's ISO2, PhoneCode contains Mobile Number's ISO2
        // For Phone Number: MobilePhoneCode (ISO2) or countryCode (dial code)
        // For Mobile Number: PhoneCode (ISO2) or mobileCountryCode (dial code)
        const phoneISO2FromResponse = formValue?.MobilePhoneCode ?? formValue?.mobilePhoneCode ?? null;
        const phoneDialCodeFromResponse = formValue?.countryCode ?? null;
        const mobileISO2FromResponse = formValue?.PhoneCode ?? formValue?.phoneCode ?? null;
        const mobileDialCodeFromResponse = formValue?.mobileCountryCode ?? null;
        
        // Check if phone codes are missing (for existing records)
        const phoneCodesMissing = !phoneISO2FromResponse && !phoneDialCodeFromResponse && !mobileISO2FromResponse && !mobileDialCodeFromResponse;
        
        // Prioritize ISO2 values, fallback to converting dial codes to ISO2
        let phoneCountryCodeISO: CountryISO | null = null;
        if (phoneISO2FromResponse) {
          // ISO2 is already in the correct format (e.g., "AL")
          phoneCountryCodeISO = phoneISO2FromResponse.toUpperCase() as CountryISO;
        } else if (phoneDialCodeFromResponse) {
          // Convert dial code to ISO2
          phoneCountryCodeISO = this.mapCallingCodeToISO(phoneDialCodeFromResponse);
        }
        
        let mobileCountryCodeISO: CountryISO | null = null;
        if (mobileISO2FromResponse) {
          // ISO2 is already in the correct format (e.g., "AF")
          mobileCountryCodeISO = mobileISO2FromResponse.toUpperCase() as CountryISO;
        } else if (mobileDialCodeFromResponse) {
          // Convert dial code to ISO2
          mobileCountryCodeISO = this.mapCallingCodeToISO(mobileDialCodeFromResponse);
        }
        
        // If phone codes are missing and branchId exists, fetch branch details to get country ID
        console.log(phoneCodesMissing,formValue?.branchId);
        
        if (phoneCodesMissing && formValue?.branchId) {
          this.fetchBranchDetailsForPhoneCodes(formValue.branchId, phone, mobileNumber);
          return; // Exit early - phone codes will be set in fetchBranchDetailsForPhoneCodes
        }
        
        const phoneCountryCode = phoneCountryCodeISO || CountryISO.UnitedStates;
        const mobileCountryCode = mobileCountryCodeISO || CountryISO.UnitedStates;
        
        // Set phone country codes FIRST (before formatting phone numbers)
        // Use setTimeout to avoid ExpressionChangedAfterItHasBeenCheckedError
        setTimeout(() => {
          this.isPhoneBindingInProgress = true;
          // Guard window: ngx-intl-tel-input can emit countryChange asynchronously after we set selectedCountryISO.
          // Keep the guard on a bit longer to prevent programmatic init from clearing already-bound values.
          if (this.phoneBindingGuardTimeout) {
            clearTimeout(this.phoneBindingGuardTimeout);
          }
          this.phoneBindingGuardTimeout = setTimeout(() => {
            this.isPhoneBindingInProgress = false;
            this.phoneBindingGuardTimeout = null;
          }, 1500);

          this.userDetailsForm.get('phoneCountryCode')?.setValue(phoneCountryCode, { emitEvent: false });
          this.userDetailsForm.get('mobileNumberCountryCode')?.setValue(mobileCountryCode, { emitEvent: false });
          
          // Set countryCode in form - use phone dial code (for backward compatibility)
          if (phoneDialCodeFromResponse) {
            this.userDetailsForm.get('countryCode')?.setValue(String(phoneDialCodeFromResponse));
          }
          
          // Format phone numbers AFTER country codes are set
          setTimeout(() => {
            this.formatPhoneNumbersForBinding(phone, mobileNumber, phoneCountryCode, mobileCountryCode);
            
            // Re-set country codes after a delay to ensure ngx-intl-tel-input initializes correctly
            // This is important when ISO2 codes are used (e.g., "AI" vs "AG" both having "+1")
            setTimeout(() => {
              if (phoneCountryCodeISO) {
                this.userDetailsForm.get('phoneCountryCode')?.setValue(phoneCountryCodeISO, { emitEvent: false });
              }
              if (mobileCountryCodeISO) {
                this.userDetailsForm.get('mobileNumberCountryCode')?.setValue(mobileCountryCodeISO, { emitEvent: false });
              }
              // Allow (user-driven) country changes to clear numbers again after binding finishes
              this.isPhoneBindingInProgress = false;
            }, 100);
          }, 100);
        }, 0);
        
        this.hideProductDetails(formValue?.companyId)
        
        
        // Store the branch ID to find country ID after branches are loaded
        if (formValue?.branchId) {
            this.pendingBranchId = formValue.branchId;
        }
    }
    
    onMobileNumberBlur() {
      const mobileCtrl = this.userDetailsForm.get('mobileNumber');
      if (mobileCtrl) {
        // Ensure formatting is applied on blur, especially for UK numbers
        const countryCode = this.userDetailsForm.get('mobileNumberCountryCode')?.value;
        if (countryCode === CountryISO.UnitedKingdom) {
          const value = mobileCtrl.value;
          if (value && typeof value === 'string') {
            const digits = value.replace(/\D/g, '');
            if (digits.length > 0) {
              const formatted = this.formatPhoneNumber(digits, countryCode);
              if (formatted && formatted !== value) {
                // Use multiple timing strategies to ensure formatting sticks
                mobileCtrl.setValue(formatted, { emitEvent: false });
                requestAnimationFrame(() => {
                  const currentValue = mobileCtrl.value;
                  if (currentValue && typeof currentValue === 'string') {
                    const currentDigits = currentValue.replace(/\D/g, '');
                    if (currentDigits === digits && currentValue !== formatted) {
                      mobileCtrl.setValue(formatted, { emitEvent: false });
                    }
                  }
                });
                setTimeout(() => {
                  const currentValue = mobileCtrl.value;
                  if (currentValue && typeof currentValue === 'string') {
                    const currentDigits = currentValue.replace(/\D/g, '');
                    if (currentDigits === digits && currentValue !== formatted) {
                      mobileCtrl.setValue(formatted, { emitEvent: false });
                    }
                  }
                }, 50);
                setTimeout(() => {
                  const currentValue = mobileCtrl.value;
                  if (currentValue && typeof currentValue === 'string') {
                    const currentDigits = currentValue.replace(/\D/g, '');
                    if (currentDigits === digits && currentValue !== formatted) {
                      mobileCtrl.setValue(formatted, { emitEvent: false });
                    }
                  }
                }, 200);
              }
            }
          }
        }
        mobileCtrl.markAsTouched();
        mobileCtrl.updateValueAndValidity();
      }
    }

    onPhoneBlur() {
      const phoneCtrl = this.userDetailsForm.get('phone');
      if (phoneCtrl) {
        // Ensure formatting is applied on blur, especially for UK numbers
        const countryCode = this.userDetailsForm.get('phoneCountryCode')?.value;
        if (countryCode === CountryISO.UnitedKingdom) {
          const value = phoneCtrl.value;
          if (value && typeof value === 'string') {
            const digits = value.replace(/\D/g, '');
            if (digits.length > 0) {
              const formatted = this.formatPhoneNumber(digits, countryCode);
              if (formatted && formatted !== value) {
                // Use multiple timing strategies to ensure formatting sticks
                phoneCtrl.setValue(formatted, { emitEvent: false });
                requestAnimationFrame(() => {
                  const currentValue = phoneCtrl.value;
                  if (currentValue && typeof currentValue === 'string') {
                    const currentDigits = currentValue.replace(/\D/g, '');
                    if (currentDigits === digits && currentValue !== formatted) {
                      phoneCtrl.setValue(formatted, { emitEvent: false });
                    }
                  }
                });
                setTimeout(() => {
                  const currentValue = phoneCtrl.value;
                  if (currentValue && typeof currentValue === 'string') {
                    const currentDigits = currentValue.replace(/\D/g, '');
                    if (currentDigits === digits && currentValue !== formatted) {
                      phoneCtrl.setValue(formatted, { emitEvent: false });
                    }
                  }
                }, 50);
                setTimeout(() => {
                  const currentValue = phoneCtrl.value;
                  if (currentValue && typeof currentValue === 'string') {
                    const currentDigits = currentValue.replace(/\D/g, '');
                    if (currentDigits === digits && currentValue !== formatted) {
                      phoneCtrl.setValue(formatted, { emitEvent: false });
                    }
                  }
                }, 200);
              }
            }
          }
        }
        phoneCtrl.markAsTouched();
        phoneCtrl.updateValueAndValidity();
      }
    }

    onPhoneCountryChange(country: any) {
        if (country && country.iso2) {
            // Ensure uppercase to avoid ExpressionChangedAfterItHasBeenCheckedError
            const iso2Upper = country.iso2.toUpperCase() as CountryISO;
            const phoneCtrl = this.userDetailsForm.get('phone');
            const currentIso = this.userDetailsForm.get('phoneCountryCode')?.value as any;
            const currentIsoUpper = currentIso ? String(currentIso).toUpperCase() : null;
            const isSameCountry = currentIsoUpper === iso2Upper;

            // Always normalize casing without triggering side effects.
            if (isSameCountry) {
              this.userDetailsForm.get('phoneCountryCode')?.setValue(iso2Upper, { emitEvent: false });
              return;
            }

            // During initial binding, ngx-intl-tel-input can emit countryChange asynchronously.
            // Also, the control is typically pristine until the user interacts.
            const isInitLikeChange =
              this.isPhoneBindingInProgress ||
              (!!phoneCtrl && phoneCtrl.pristine && !phoneCtrl.touched && !phoneCtrl.dirty);

            this.userDetailsForm.get('phoneCountryCode')?.setValue(iso2Upper, { emitEvent: !isInitLikeChange });

            if (!isInitLikeChange) {
              // Clear only for user-driven country changes (actual change).
              this.userDetailsForm.get('phone')?.setValue('', { emitEvent: false });
              setTimeout(() => {
                const ctrl = this.userDetailsForm.get('phone');
                ctrl?.updateValueAndValidity();
              }, 100);
            }
        }
    }
    
    onMobileNumberCountryChange(country: any) {
        if (country && country.iso2) {
            // Ensure uppercase to avoid ExpressionChangedAfterItHasBeenCheckedError
            const iso2Upper = country.iso2.toUpperCase() as CountryISO;
            const mobileCtrl = this.userDetailsForm.get('mobileNumber');
            const currentIso = this.userDetailsForm.get('mobileNumberCountryCode')?.value as any;
            const currentIsoUpper = currentIso ? String(currentIso).toUpperCase() : null;
            const isSameCountry = currentIsoUpper === iso2Upper;

            // Always normalize casing without triggering side effects.
            if (isSameCountry) {
              this.userDetailsForm.get('mobileNumberCountryCode')?.setValue(iso2Upper, { emitEvent: false });
              return;
            }

            const isInitLikeChange =
              this.isPhoneBindingInProgress ||
              (!!mobileCtrl && mobileCtrl.pristine && !mobileCtrl.touched && !mobileCtrl.dirty);

            this.userDetailsForm.get('mobileNumberCountryCode')?.setValue(iso2Upper, { emitEvent: !isInitLikeChange });

            if (!isInitLikeChange) {
              // Clear only for user-driven country changes (actual change).
              this.userDetailsForm.get('mobileNumber')?.setValue('', { emitEvent: false });
              setTimeout(() => {
                const ctrl = this.userDetailsForm.get('mobileNumber');
                ctrl?.updateValueAndValidity();
              }, 100);
            }
        }
    }
    
    private formatPhoneNumbersForBinding(phone: any, mobileNumber: any, phoneCountryCode?: CountryISO, mobileCountryCode?: CountryISO) {
        // Convert phone numbers to string if they're numbers or objects
        let phoneStr: string | null = null;
        let mobileNumberStr: string | null = null;
        
        if (phone !== null && phone !== undefined) {
          if (typeof phone === 'object' && phone.number !== undefined) {
            phoneStr = String(phone.number);
          } else {
            phoneStr = String(phone);
          }
        }
        
        if (mobileNumber !== null && mobileNumber !== undefined) {
          if (typeof mobileNumber === 'object' && mobileNumber.number !== undefined) {
            mobileNumberStr = String(mobileNumber.number);
          } else {
            mobileNumberStr = String(mobileNumber);
          }
        }
        
        // Use provided country codes or get from form (should already be set)
        const phoneCountryCodeToUse = phoneCountryCode || this.userDetailsForm.get('phoneCountryCode')?.value || CountryISO.UnitedStates;
        const mobileCountryCodeToUse = mobileCountryCode || this.userDetailsForm.get('mobileNumberCountryCode')?.value || CountryISO.UnitedStates;
        
        // Format phone numbers (string format when separateDialCode is true)
        const phoneRaw = phoneStr ? this.extractRawPhoneNumber(phoneStr, phoneCountryCodeToUse) : null;
        const mobileRaw = mobileNumberStr ? this.extractRawPhoneNumber(mobileNumberStr, mobileCountryCodeToUse) : null;
        
        const formattedPhone = phoneRaw ? this.formatPhoneNumber(phoneRaw, phoneCountryCodeToUse) : null;
        const formattedMobile = mobileRaw ? this.formatPhoneNumber(mobileRaw, mobileCountryCodeToUse) : null;
        
        // Set as string values (when separateDialCode is true)
        this.userDetailsForm.get('phone')?.setValue(formattedPhone || '', { emitEvent: false });
        this.userDetailsForm.get('mobileNumber')?.setValue(formattedMobile || '', { emitEvent: false });
    }
    
    extractRawPhoneNumber(formattedPhoneNumber: string | number | null | undefined, countryCode?: CountryISO | null): string | null {
        if (formattedPhoneNumber === null || formattedPhoneNumber === undefined) {
            return null;
        }
        
        const phoneStr = typeof formattedPhoneNumber === 'number' ? String(formattedPhoneNumber) : formattedPhoneNumber;
        
        if (!phoneStr) {
            return null;
        }
        
        // Extract all digits first
        let cleaned = phoneStr.replace(/\D/g, '');
        
        if (!cleaned || cleaned.length === 0) {
            return null;
        }
        
        const phoneCountryCode = this.userDetailsForm.get('phoneCountryCode')?.value;
        const mobileCountryCode = this.userDetailsForm.get('mobileNumberCountryCode')?.value;
        const selectedCountryCode = countryCode || phoneCountryCode || mobileCountryCode;
        
        if (!selectedCountryCode) {
            return cleaned;
        }

        // Try to parse and extract national number
        try {
            const regionCode = String(selectedCountryCode).toLowerCase();
            if (!regionCode) {
                return cleaned;
            }
            const parsedNumber = this.phoneUtil.parse(cleaned, regionCode);
            
            // Get national number (without country code)
            const nationalNumber = parsedNumber.getNationalNumber();
            if (nationalNumber) {
                return String(nationalNumber);
            }
        } catch (error) {
            // Parsing failed - might be incomplete number, try to remove country code prefix manually
        }
        
        // Fallback: remove country code prefix if present
        const countryCodePrefix = this.getCountryCodePrefix(selectedCountryCode);
        if (countryCodePrefix) {
            const prefixDigits = countryCodePrefix.replace(/\D/g, '');
            // Check if the cleaned number starts with the country code prefix
            if (cleaned.startsWith(prefixDigits)) {
                // Remove the prefix to get national number
                cleaned = cleaned.substring(prefixDigits.length);
                // For UK (44), also remove leading 0 if present (UK national format starts with 0)
                if (selectedCountryCode === CountryISO.UnitedKingdom && cleaned.startsWith('0')) {
                    cleaned = cleaned.substring(1);
                }
            }
        }
        
        return cleaned;
    }
    
    formatPhoneNumber(phoneNumber: string | number | null | undefined, countryCode?: CountryISO | null): string | null {
        if (typeof phoneNumber === "number") {
            phoneNumber = phoneNumber.toString();
        }

        if (!phoneNumber || typeof phoneNumber !== "string") {
            return null;
        }

        const selectedCountryCode = countryCode || CountryISO.UnitedStates;
        
        if (!selectedCountryCode) {
            return phoneNumber;
        }

        try {
            const cleanedNumber = phoneNumber.replace(/\D/g, '');
            const regionCode = String(selectedCountryCode).toLowerCase();
            
            // Special handling for UK to ensure consistent formatting - ALWAYS use this for UK
            if (selectedCountryCode === CountryISO.UnitedKingdom) {
                // UK numbers: 10 digits (mobile) or 11 digits (landline)
                if (cleanedNumber.length <= 4) {
                    return cleanedNumber;
                } else if (cleanedNumber.length <= 7) {
                    return `${cleanedNumber.slice(0, 4)} ${cleanedNumber.slice(4)}`;
                } else if (cleanedNumber.length === 10) {
                    // 10 digits: XXXX XXXXXX (e.g., 7655 677666)
                    return `${cleanedNumber.slice(0, 4)} ${cleanedNumber.slice(4)}`;
                } else if (cleanedNumber.length === 11) {
                    // 11 digits: XXXX XXX XXXX (e.g., 7655 677 6666)
                    return `${cleanedNumber.slice(0, 4)} ${cleanedNumber.slice(4, 7)} ${cleanedNumber.slice(7, 11)}`;
                } else {
                    // More than 11 digits: truncate to 11 and format
                    const truncated = cleanedNumber.substring(0, 11);
                    return `${truncated.slice(0, 4)} ${truncated.slice(4, 7)} ${truncated.slice(7, 11)}`;
                }
            }
            
            const parsedNumber = this.phoneUtil.parse(cleanedNumber, regionCode);
            
            try {
                const formatted = this.phoneUtil.format(parsedNumber, PhoneNumberFormat.NATIONAL);
                // Remove any leading country code that might be included
                let finalFormatted = formatted.replace(/^\+\d+\s*/, '').trim();
                // Remove leading "0" that NATIONAL format adds for some countries (e.g., Afghanistan, UK)
                // Since country code is displayed separately, we don't need this leading "0"
                finalFormatted = finalFormatted.replace(/^0+/, '').trim();
                return finalFormatted;
            } catch {
                return this.formatPhoneNumberProgressive(cleanedNumber, selectedCountryCode);
            }
        } catch (error) {
            return this.formatPhoneNumberFallback(phoneNumber, selectedCountryCode);
        }
    }
    
    private formatPhoneNumberProgressive(cleanedNumber: string, countryCode: CountryISO | null | undefined): string {
        if (!countryCode || !cleanedNumber) {
            return cleanedNumber;
        }
        
        if (countryCode === CountryISO.UnitedStates) {
            if (cleanedNumber.length <= 3) {
                return cleanedNumber.length > 0 ? `(${cleanedNumber}` : '';
            } else if (cleanedNumber.length <= 6) {
                return `(${cleanedNumber.slice(0, 3)}) ${cleanedNumber.slice(3)}`;
            } else {
                return `(${cleanedNumber.slice(0, 3)}) ${cleanedNumber.slice(3, 6)}-${cleanedNumber.slice(6, 10)}`;
            }
        } else if (countryCode === CountryISO.UnitedKingdom) {
            if (cleanedNumber.length <= 4) {
                return cleanedNumber;
            } else if (cleanedNumber.length <= 7) {
                return `${cleanedNumber.slice(0, 4)} ${cleanedNumber.slice(4)}`;
            } else {
                return `${cleanedNumber.slice(0, 4)} ${cleanedNumber.slice(4, 7)} ${cleanedNumber.slice(7, 11)}`;
            }
        }
        
        return cleanedNumber;
    }
    
    private formatPhoneNumberFallback(phoneNumber: string, countryCode: CountryISO | null | undefined): string {
        if (!countryCode) {
            return phoneNumber;
        }
        
        const cleanedNumber = phoneNumber.replace(/\D/g, '');
        const countryCodePrefix = this.getCountryCodePrefix(countryCode);

        let digits = cleanedNumber;
        if (countryCodePrefix) {
            const prefixDigits = countryCodePrefix.replace(/\D/g, '');
            if (digits.startsWith(prefixDigits)) {
                digits = digits.substring(prefixDigits.length);
            }
        }

        if (countryCode === CountryISO.UnitedStates && digits.length === 10) {
            const match = digits.match(/^(\d{3})(\d{3})(\d{4})$/);
            if (match) {
                return `(${match[1]}) ${match[2]}-${match[3]}`;
            }
        } else if (countryCode === CountryISO.UnitedStates && digits.length > 0) {
            return this.formatPhoneNumberProgressive(digits, countryCode);
        } else if (countryCode === CountryISO.UnitedKingdom && digits.length >= 10) {
            if (digits.length === 11) {
                const match = digits.match(/^(\d{4})(\d{3})(\d{4})$/);
                if (match) {
                    return `${match[1]} ${match[2]} ${match[3]}`;
                }
            }
            return this.formatPhoneNumberProgressive(digits, countryCode);
        }

        return digits;
    }
    
    private getCountryCodePrefix(countryCode: CountryISO | null | undefined): string {
        if (!countryCode) {
            return '';
        }
        try {
            const callingCode = this.phoneUtil.getCountryCodeForRegion(String(countryCode).toLowerCase());
            if (callingCode) {
                return `+${callingCode}`;
            }
            return '';
        } catch {
            return '';
        }
    }
    
    mapCallingCodeToISO(callingCode: string | number | null): CountryISO | null {
        if (!callingCode) return null;
        
        const codeStr = String(callingCode).replace(/^\+/, '').trim();
        const codeNum = parseInt(codeStr, 10);
        
        if (isNaN(codeNum)) return null;
        
        try {
            const supportedRegions = this.phoneUtil.getSupportedRegions();
            for (const region of supportedRegions) {
                try {
                    const regionCallingCode = this.phoneUtil.getCountryCodeForRegion(region);
                    if (regionCallingCode === codeNum) {
                        return region.toUpperCase() as CountryISO;
                    }
                } catch {
                    continue;
                }
            }
        } catch (error) {
           
        }
        
        // Fallback: try to match common codes manually
        if (codeNum === 1) return CountryISO.UnitedStates;
        if (codeNum === 44) return CountryISO.UnitedKingdom;
        if (codeNum === 93) return CountryISO.Afghanistan;
        if (codeNum === 355) return CountryISO.Albania;
        
        return null;
    }

    hideProductDetails(selectedCompanyId : number){
        const selectedCompany = this.companyList().find(company => company.companyId === selectedCompanyId);

        if (selectedCompany) {
            const companyTypeId = selectedCompany.companyType;

            const companyTypes = this.systemAdminService.getCompanyTypeList(); // Fetch from local storage

            if (companyTypes[companyTypeId] && companyTypes[companyTypeId].code === "Admin Company") {
            
                this.isAdminCompany.emit(true);
            } else {
               
                this.isAdminCompany.emit(false);
            }
        }
    }

    onCompanyChange(event: Event) {
        this.userDetailsForm.patchValue({branchId: null});
        this.selectedBranchCountryId.set(null); // Reset country ID when company changes
        this.pendingBranchId = null; // Clear pending branch ID when company changes
        const selectedCompanyId = Number((event.target as HTMLSelectElement).value);
        this.hideProductDetails(selectedCompanyId)
    }

    onCheckboxChange(event: Event): void {
        const isChecked = (event.target as HTMLInputElement).checked;
        this.userDetailsForm.get('isActive')?.setValue(isChecked ? 1 : 0);
    }
    

    getBranchDetailsByCompanyId(companyId: string) {
        this.systemAdminService.getBranchByCompanyId(companyId).subscribe({
            next: (response: BranchDetailsResponse[]) => {
                if(response){
                    this.branchList.set(response);
                    
                    // If there's a pending branch ID, find its country ID
                    // Phone number formatting is now handled by add-user component
                    if (this.pendingBranchId) {
                        const pendingBranchIdValue = this.pendingBranchId; // Capture value before clearing
                        this.findCountryIdForBranch(pendingBranchIdValue);
                        // Wait a bit for countryCode to be set
                        setTimeout(() => {
                            // Retry setting countryCode if it wasn't set (country list might not have been loaded)
                            const currentCountryCode = this.userDetailsForm.get('countryCode')?.value;
                            if (!currentCountryCode && this.countryList().length > 0) {
                                this.findCountryIdForBranch(pendingBranchIdValue);
                            }
                        }, 200);
                        this.pendingBranchId = null; // Clear the pending branch ID
                    } else {
                        // Fallback: use current form branchId if present
                        const currentBranchId = this.userDetailsForm.get('branchId')?.value;
                        if (currentBranchId) {
                            this.findCountryIdForBranch(Number(currentBranchId));
                            setTimeout(() => {
                                // Retry setting countryCode if it wasn't set
                                const currentCountryCode = this.userDetailsForm.get('countryCode')?.value;
                                if (!currentCountryCode && this.countryList().length > 0) {
                                    this.findCountryIdForBranch(Number(currentBranchId));
                                }
                            }, 200);
                        }
                    }
                }
            },
            error: (error: any) => {
                this.branchList.set([]);
                this.userDetailsForm.patchValue({branchId: null});
                const msg = error?.status === 404  ? "No branches found for the specified company" :
                "System error. Failed to retrieve data. Please exit and contact help@AbacusAU.com."
                this.toastService.addNotification({message: msg, type: ToastType.Error});
            }
        });
    }

    findCountryIdForBranch(branchId: number) {
        const branch = this.branchList().find(b => b.branchId === branchId);
        const countryId = (branch as any)?.address?.countryId ?? (branch as any)?.branchAddress?.countryId;
        if (countryId != null) {
            this.selectedBranchCountryId.set(countryId);
            
            // Update countryCode in form based on countryId
            if (this.countryList().length > 0) {
                const selectedCountry = this.countryList().find(c => Number(c.countryId) === Number(countryId));
                const countryCode = selectedCountry?.isdCode ? String(selectedCountry.isdCode) : null;
                this.userDetailsForm.get('countryCode')?.setValue(countryCode);
            } else {
                // If country list not loaded yet, wait a bit and try again
                setTimeout(() => {
                    if (this.countryList().length > 0) {
                        const selectedCountry = this.countryList().find(c => Number(c.countryId) === Number(countryId));
                        const countryCode = selectedCountry?.isdCode ? String(selectedCountry.isdCode) : null;
                        this.userDetailsForm.get('countryCode')?.setValue(countryCode);
                    }
                }, 100);
            }
        } else {
            if (branch) {
            }
        }
    }
    
    // Fetch branch details to get country ID when phone codes are missing
    private fetchBranchDetailsForPhoneCodes(branchId: number, phone: string | null, mobileNumber: string | null) {
        this.systemAdminService.getBranchDetails(String(branchId)).subscribe({
            next: (branchResponse: BranchDetailsResponse) => {
                if (branchResponse) {
                    // Extract country ID from branch address
                    const countryId = branchResponse?.branchAddress?.countryId ?? null;
                    
                    if (countryId && this.countryList().length > 0) {
                        // Find country from country list
                        const selectedCountry = this.countryList().find(c => Number(c.countryId) === Number(countryId));
                        
                        if (selectedCountry) {
                            // Convert country ID to CountryISO
                            const countryISO = this.getCountryCodeFromCountryId(countryId);
                            
                            if (countryISO) {
                                // Set both phone and mobile country codes to the branch's country
                                // Use setTimeout to avoid ExpressionChangedAfterItHasBeenCheckedError
                                setTimeout(() => {
                                    this.userDetailsForm.get('phoneCountryCode')?.setValue(countryISO, { emitEvent: false });
                                    this.userDetailsForm.get('mobileNumberCountryCode')?.setValue(countryISO, { emitEvent: false });
                                    
                                    // Set countryCode (dial code) for backward compatibility
                                    const countryCode = selectedCountry?.isdCode ? String(selectedCountry.isdCode) : null;
                                    if (countryCode) {
                                        this.userDetailsForm.get('countryCode')?.setValue(countryCode);
                                    }
                                    
                                    // Format phone numbers AFTER country codes are set
                                    setTimeout(() => {
                                        this.formatPhoneNumbersForBinding(phone, mobileNumber, countryISO, countryISO);
                                        
                                        // Re-set country codes after a delay to ensure ngx-intl-tel-input initializes correctly
                                        setTimeout(() => {
                                            this.userDetailsForm.get('phoneCountryCode')?.setValue(countryISO, { emitEvent: false });
                                            this.userDetailsForm.get('mobileNumberCountryCode')?.setValue(countryISO, { emitEvent: false });
                                        }, 100);
                                    }, 100);
                                }, 0);
                            } else {
                                // Fallback to US if country not found
                                this.setDefaultPhoneCodes(phone, mobileNumber);
                            }
                        } else {
                            // Fallback to US if country not found in list
                            this.setDefaultPhoneCodes(phone, mobileNumber);
                        }
                    } else {
                        // Fallback to US if country ID not found or country list not loaded
                        this.setDefaultPhoneCodes(phone, mobileNumber);
                    }
                } else {
                    // Fallback to US if branch details not found
                    this.setDefaultPhoneCodes(phone, mobileNumber);
                }
            },
            error: (error: any) => {
                console.log("Error fetching branch details for phone codes:", error);
                // Fallback to US on error
                this.setDefaultPhoneCodes(phone, mobileNumber);
            }
        });
    }
    
    // Helper method to set default phone codes (US)
    private setDefaultPhoneCodes(phone: string | null, mobileNumber: string | null) {
        const defaultCountryCode = CountryISO.UnitedStates;
        // Use setTimeout to avoid ExpressionChangedAfterItHasBeenCheckedError
        setTimeout(() => {
            this.userDetailsForm.get('phoneCountryCode')?.setValue(defaultCountryCode, { emitEvent: false });
            this.userDetailsForm.get('mobileNumberCountryCode')?.setValue(defaultCountryCode, { emitEvent: false });
            
            setTimeout(() => {
                this.formatPhoneNumbersForBinding(phone, mobileNumber, defaultCountryCode, defaultCountryCode);
            }, 100);
        }, 0);
    }
    
    // Convert country ID to CountryISO
    private getCountryCodeFromCountryId(countryId: number | null): CountryISO | null {
        if (!countryId) return null;
        
        // Common mappings
        if (countryId === 1) return CountryISO.UnitedStates;
        if (countryId === 2) return CountryISO.UnitedKingdom;
        
        // Try to find country in country list and get ISO2 code
        if (this.countryList().length > 0) {
            const country = this.countryList().find(c => Number(c.countryId) === Number(countryId));
            if (country && (country as any).iso2) {
                return (country as any).iso2.toUpperCase() as CountryISO;
            }
        }
        
        return null;
    }


    getCountryList() {
        this.systemAdminService.getCountryList().subscribe({
            next: (response: any) => {
                if(response){
                    this.countryList.set(response);
                    // If country list is loaded and there's already a branch selected, set countryCode
                    const currentBranchId = this.userDetailsForm.get('branchId')?.value;
                    if (currentBranchId && this.branchList().length > 0) {
                        this.findCountryIdForBranch(Number(currentBranchId));
                    }
                }
            },
            error: (error: any) => {
                console.log("error", error);
            }
        })
    }

    getCompanyBranchAndUserRoleList() {    
        forkJoin([
            this.systemAdminService.getAllCompanyList(),
            this.systemAdminService.getUserRoleList()
        ]).pipe(
            map(([company, userRole]) => {
                return {company, userRole};
            })
        ).subscribe({
            next: (response: any) => {
                if(response){
                    this.companyList.set(response.company);
                    this.userRoleList.set(response.userRole);
                }
            },
            error: (error: any) => {
            }
        })
    }


    onCancel() {
        this.cancelEmitter.emit(true);  
    }

    onSubmit() {
        // Mark all fields as touched to show validation errors
        // Note: updateValueAndValidity is called in onUserDetailsSubmit to avoid triggering valueChanges
        Object.keys(this.userDetailsForm.controls).forEach(key => {
            const control = this.userDetailsForm.get(key);
            if (control) {
                control.markAsTouched();
            }
        });
        
        // Phone number cleaning is now handled by add-user component's extractRawPhoneNumber
        // No need to clean here as the form values are already in the correct format
        // Validation and value updates are handled in onUserDetailsSubmit
        this.userDetailsSubmitEmitter.emit(true);
    }

    ngOnDestroy(): void {
        this.companySubscription.unsubscribe();
        this.branchSubscription.unsubscribe();
        this.mobileInputSubscription.unsubscribe();
        this.phoneInputSubscription.unsubscribe();
        if (this.phoneBindingGuardTimeout) {
          clearTimeout(this.phoneBindingGuardTimeout);
          this.phoneBindingGuardTimeout = null;
        }
    }
 
}